import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { authResponseSchema, type PublicUser } from '@competa/shared';
import { ApiRequestError, apiRequest, setUnauthorizedHandler } from '../../api/client';

/** Schlüssel der Abfrage des angemeldeten Benutzers im Query-Cache. */
export const AUTH_QUERY_KEY = ['auth', 'me'] as const;

/** Anzahl Tage vor Ablauf, ab der eine Warnung angezeigt wird. */
const EXPIRY_WARNING_DAYS = 10;

/** Werte, die der Auth-Kontext bereitstellt. */
interface AuthContextValue {
  /** Angemeldeter Benutzer, `null` wenn abgemeldet, `undefined` solange der Status geladen wird. */
  user: PublicUser | null | undefined;
  /** Meldet an; wirft `ApiRequestError` bei Fehlschlag. */
  login: (username: string, password: string) => Promise<PublicUser>;
  /** Meldet ab und räumt alle zwischengespeicherten Daten auf. */
  logout: () => Promise<void>;
  /** Übernimmt ein geändertes Benutzerprofil (z. B. nach dem Passwortwechsel) in den Cache. */
  setUser: (user: PublicUser) => void;
  /** Tage bis zum Ablauf des Zugangs, wenn die Warnung angezeigt werden soll (nach dem Login), sonst `null`. */
  expiryWarningDays: number | null;
  /** Blendet die Ablaufwarnung aus. */
  dismissExpiryWarning: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Berechnet die verbleibenden Tage bis zu einem Ablaufdatum.
 * @param expiresAt Ablaufdatum (ISO-8601) oder `null`.
 * @param now Aktuelle Zeit.
 * @returns Aufgerundete Tage (negativ = abgelaufen) oder `null` bei unbefristet.
 */
export function daysUntil(expiresAt: string | null, now: Date = new Date()): number | null {
  if (!expiresAt) return null;
  return Math.ceil((new Date(expiresAt).getTime() - now.getTime()) / 86_400_000);
}

/**
 * Stellt Anmeldestatus und Aktionen bereit. Der Status stammt aus `GET /api/auth/me`; ein 401
 * bedeutet „abgemeldet". Läuft die Sitzung während der Nutzung ab, setzt der globale Handler des
 * API-Clients den Status auf „abgemeldet" zurück, woraufhin die Routenwächter zur Anmeldung leiten.
 * @param props.children Die umschlossene Anwendung.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [expiryWarningDays, setExpiryWarningDays] = useState<number | null>(null);

  const me = useQuery({
    queryKey: AUTH_QUERY_KEY,
    queryFn: async (): Promise<PublicUser | null> => {
      try {
        return (await apiRequest('GET', '/auth/me', { schema: authResponseSchema })).user;
      } catch (error) {
        if (error instanceof ApiRequestError && error.status === 401) return null;
        throw error;
      }
    },
    retry: false,
    staleTime: Infinity,
  });

  useEffect(() => {
    setUnauthorizedHandler(() => {
      queryClient.setQueryData(AUTH_QUERY_KEY, null);
    });
    return () => setUnauthorizedHandler(null);
  }, [queryClient]);

  const loginMutation = useMutation({
    mutationFn: (credentials: { username: string; password: string }) =>
      apiRequest('POST', '/auth/login', { body: credentials, schema: authResponseSchema }),
  });

  const login = useCallback(
    async (username: string, password: string) => {
      const { user } = await loginMutation.mutateAsync({ username, password });
      queryClient.setQueryData(AUTH_QUERY_KEY, user);
      const days = daysUntil(user.expiresAt);
      setExpiryWarningDays(days !== null && days > 0 && days <= EXPIRY_WARNING_DAYS ? days : null);
      return user;
    },
    [loginMutation, queryClient],
  );

  const logout = useCallback(async () => {
    try {
      await apiRequest('POST', '/auth/logout');
    } finally {
      // Auch bei Netzwerkfehlern lokal abmelden und keine Fremddaten im Cache behalten: alle
      // Fachdaten verwerfen, den Anmeldestatus gezielt auf „abgemeldet" setzen (kein Neuladen).
      setExpiryWarningDays(null);
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== AUTH_QUERY_KEY[0] });
      queryClient.setQueryData(AUTH_QUERY_KEY, null);
    }
  }, [queryClient]);

  const setUser = useCallback(
    (user: PublicUser) => {
      queryClient.setQueryData(AUTH_QUERY_KEY, user);
    },
    [queryClient],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user: me.isPending ? undefined : (me.data ?? null),
      login,
      logout,
      setUser,
      expiryWarningDays,
      dismissExpiryWarning: () => setExpiryWarningDays(null),
    }),
    [me.isPending, me.data, login, logout, setUser, expiryWarningDays],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Zugriff auf Anmeldestatus und Aktionen.
 * @returns Benutzer, Login, Logout und Ablaufwarnung.
 * @throws Error, wenn kein `AuthProvider` vorhanden ist.
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth muss innerhalb eines AuthProvider verwendet werden');
  return context;
}
